import { http } from '@/shared/api/api-client';
import type { PagedResponse } from '@/shared/api/api-types';

import type {
  ChannelDailyReport,
  ChannelDailyRow,
  FinancialIssuesReport,
  FinancialTransaction,
  FinancialTransactionParams,
  FinancialReport,
  FinancialRow,
  OperationalReport,
  PartnerShareReport,
  PartnerShareRow,
  SettlementSummaryRow,
  PricingReport,
  ProviderReport,
  ReportFilterOption,
  ReportFilterOptions,
  ReportParams,
} from '../types/report.types';

/**
 * Reports — UT system `AdminApis/Reports`. The backend limits every report to
 * the staff member's countries, builds it from what happened at the time of
 * each order, and answers 403 for a report the role may not see.
 */

export async function getOperationalReport(
  params: ReportParams,
  signal?: AbortSignal,
): Promise<OperationalReport> {
  return http.get<OperationalReport>('/admin/reports/operational', { params, signal });
}

/**
 * The financial row as the API sends it. The waterfall fields after `revenue`
 * arrived with settlements; an older backend omits them, so they are optional
 * on the wire and defaulted here (collected = revenue, which is what it is).
 */
type FinancialRowWire = Pick<
  FinancialRow,
  | 'key'
  | 'label'
  | 'currencyCode'
  | 'completedOrders'
  | 'revenue'
  | 'pricedOrders'
  | 'netRevenue'
  | 'actualCost'
  | 'grossProfit'
  | 'realizedMarginPct'
> &
  Partial<FinancialRow>;

type FinancialReportWire = Omit<FinancialReport, 'totals' | 'rows' | 'settlement'> & {
  totals: FinancialRowWire[];
  rows: FinancialRowWire[];
  settlement?: SettlementSummaryRow[] | null;
};

function toFinancialRow(row: FinancialRowWire): FinancialRow {
  return {
    key: row.key,
    label: row.label,
    currencyCode: row.currencyCode,
    completedOrders: row.completedOrders,
    revenue: row.revenue,
    collected: row.collected ?? row.revenue,
    pricedOrders: row.pricedOrders,
    discounts: row.discounts ?? 0,
    channelFees: row.channelFees ?? 0,
    netRevenue: row.netRevenue,
    actualCost: row.actualCost,
    grossProfit: row.grossProfit,
    mgmtFee: row.mgmtFee ?? 0,
    netProfit: row.netProfit ?? 0,
    partnerShare: row.partnerShare ?? 0,
    retained: row.retained ?? 0,
    refundsCount: row.refundsCount ?? 0,
    refundsAmount: row.refundsAmount ?? 0,
    collectedAfterRefunds: row.collectedAfterRefunds ?? row.revenue,
    realizedMarginPct: row.realizedMarginPct,
  };
}

function toFinancialReport(report: FinancialReportWire): FinancialReport {
  return {
    from: report.from,
    to: report.to,
    groupBy: report.groupBy,
    totals: report.totals.map(toFinancialRow),
    rows: report.rows.map(toFinancialRow),
    refunds: report.refunds,
    settlementAvailable: report.settlementAvailable && Array.isArray(report.settlement),
    settlement: report.settlement ?? [],
  };
}

export async function getFinancialReport(
  params: ReportParams,
  signal?: AbortSignal,
): Promise<FinancialReport> {
  return toFinancialReport(
    await http.get<FinancialReportWire>('/admin/reports/financial', { params, signal }),
  );
}

/**
 * Loss and below-target orders, refunds, unpriced orders and open failed
 * payments per day or month and currency, plus outstanding settlement
 * (reports.financial.read). Any grouping other than day/month is sent as day.
 */
export async function getFinancialIssues(
  params: ReportParams,
  signal?: AbortSignal,
): Promise<FinancialIssuesReport> {
  const groupBy = params.groupBy === 'month' ? 'month' : 'day';
  const report = await http.get<FinancialIssuesReport>('/admin/reports/financial/issues', {
    params: { ...params, groupBy },
    signal,
  });
  return {
    from: report.from,
    to: report.to,
    groupBy: report.groupBy === 'month' ? 'month' : 'day',
    series: report.series ?? [],
    totals: report.totals ?? [],
    outstanding: report.outstanding ?? [],
    aging: report.aging ?? [],
    outstandingTotals: report.outstandingTotals ?? [],
  };
}

/** A channel-daily row as the API sends it: day and channel are null on the totals. */
type ChannelDailyRowWire = Omit<
  ChannelDailyRow,
  'day' | 'channelCode' | 'channelName' | 'averageOrder'
> & {
  day?: string | null;
  channelCode?: string | null;
  channelName?: string | null;
  averageOrder?: number | null;
};

type ChannelDailyReportWire = {
  from: string;
  to: string;
  rows?: ChannelDailyRowWire[] | null;
  channelTotals?: ChannelDailyRowWire[] | null;
  dayTotals?: ChannelDailyRowWire[] | null;
  totals?: ChannelDailyRowWire[] | null;
  unpricedOrders?: number | null;
};

function toChannelDailyRow(row: ChannelDailyRowWire): ChannelDailyRow {
  return {
    day: row.day ?? null,
    channelCode: row.channelCode ?? null,
    channelName: row.channelName ?? null,
    currencyCode: row.currencyCode,
    orders: row.orders,
    collected: row.collected,
    discounts: row.discounts,
    channelFees: row.channelFees,
    netExpected: row.netExpected,
    averageOrder: row.averageOrder ?? null,
  };
}

/** The channel-daily report is always by day: any other grouping would only split its cache. */
const byDay = (params: ReportParams): ReportParams => ({ ...params, groupBy: 'day' });

/**
 * Each day on each payment channel, per currency: orders, collected, discounts,
 * expected channel fees and the net the channel should transfer, with totals
 * per channel, per day and overall (reports.financial.read).
 */
export async function getChannelDailyReport(
  params: ReportParams,
  signal?: AbortSignal,
): Promise<ChannelDailyReport> {
  const report = await http.get<ChannelDailyReportWire>('/admin/reports/financial/channels-daily', {
    params: byDay(params),
    signal,
  });
  return {
    from: report.from,
    to: report.to,
    rows: (report.rows ?? []).map(toChannelDailyRow),
    channelTotals: (report.channelTotals ?? []).map(toChannelDailyRow),
    dayTotals: (report.dayTotals ?? []).map(toChannelDailyRow),
    totals: (report.totals ?? []).map(toChannelDailyRow),
    unpricedOrders: report.unpricedOrders ?? 0,
  };
}

/** The channel-daily report as Excel (reports.export + reports.financial.read). */
export async function exportChannelDailyReport(params: ReportParams) {
  const { blob, fileName } = await http.download('/admin/reports/financial/channels-daily/export', {
    params: { ...byDay(params), format: 'xlsx' },
  });
  return { blob, fileName: fileName ?? `channels-daily-${params.from}-${params.to}.xlsx` };
}

type PartnerShareRowWire = Omit<
  PartnerShareRow,
  'countryName' | 'partnerName' | 'sharePct' | 'grossProfit' | 'mgmtFee'
> & {
  countryName?: string | null;
  grossProfit?: number | null;
  mgmtFee?: number | null;
  partnerName?: string | null;
  sharePct?: number | null;
};

type PartnerShareReportWire = {
  from: string;
  to: string;
  groupBy?: string | null;
  rows?: PartnerShareRowWire[] | null;
  totals?: PartnerShareRowWire[] | null;
  unpricedOrders?: number | null;
};

function toPartnerShareRow(row: PartnerShareRowWire): PartnerShareRow {
  return {
    key: row.key,
    countryId: row.countryId,
    countryName: row.countryName ?? null,
    partnerName: row.partnerName?.trim() ? row.partnerName : null,
    currencyCode: row.currencyCode,
    orders: row.orders,
    collected: row.collected,
    grossProfit: row.grossProfit ?? null,
    mgmtFee: row.mgmtFee ?? null,
    netProfit: row.netProfit,
    sharePct: row.sharePct ?? null,
    partnerShare: row.partnerShare,
    refundsCount: row.refundsCount,
    refundAdjustment: row.refundAdjustment,
    netShare: row.netShare,
  };
}

/** The partner-share report groups by day or month; anything else is sent as day. */
const dayOrMonth = (params: ReportParams): ReportParams => ({
  ...params,
  groupBy: params.groupBy === 'month' ? 'month' : 'day',
});

/**
 * The partner's share per day or month and country: gross profit, management
 * fee, net profit, share %, the share, what refunds paid gave back and the net
 * share, with totals per country and currency (reports.financial.read). It is
 * also the Partner's own report (reports.partner.read): the API scopes it to
 * the partner's countries and leaves out gross profit and management fee.
 */
export async function getPartnerShareReport(
  params: ReportParams,
  signal?: AbortSignal,
): Promise<PartnerShareReport> {
  const report = await http.get<PartnerShareReportWire>('/admin/reports/financial/partner-share', {
    params: dayOrMonth(params),
    signal,
  });
  return {
    from: report.from,
    to: report.to,
    groupBy: report.groupBy === 'month' ? 'month' : 'day',
    rows: (report.rows ?? []).map(toPartnerShareRow),
    totals: (report.totals ?? []).map(toPartnerShareRow),
    unpricedOrders: report.unpricedOrders ?? 0,
  };
}

/** The partner-share report as Excel (reports.export + reports.financial.read). */
export async function exportPartnerShareReport(params: ReportParams) {
  const { blob, fileName } = await http.download('/admin/reports/financial/partner-share/export', {
    params: { ...dayOrMonth(params), format: 'xlsx' },
  });
  return { blob, fileName: fileName ?? `partner-share-${params.from}-${params.to}.xlsx` };
}

/** Margin vs target, price health, guardrails and procurement saving (reports.pricing.read). */
export async function getPricingReport(
  params: ReportParams,
  signal?: AbortSignal,
): Promise<PricingReport> {
  return http.get<PricingReport>('/admin/reports/pricing', { params, signal });
}

/** Each provider's orders, success rate, share and wins (reports.providers.read). */
export async function getProviderReport(
  params: ReportParams,
  signal?: AbortSignal,
): Promise<ProviderReport> {
  return http.get<ProviderReport>('/admin/reports/providers', { params, signal });
}

export type ReportKind = 'operational' | 'financial' | 'pricing' | 'providers';

/** The report as Excel (reports.export). Recorded in the audit log by the backend. */
export async function exportReport(kind: ReportKind, params: ReportParams) {
  const { blob, fileName } = await http.download(`/admin/reports/${kind}/export`, {
    params: { ...params, format: 'xlsx' },
  });
  return { blob, fileName: fileName ?? `${kind}-${params.from}-${params.to}.xlsx` };
}

/** The Admin API's page shape; `totalCount` maps to the app's `totalItems`. */
interface AdminPage<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

function toFinancialTransaction(row: FinancialTransaction): FinancialTransaction {
  return {
    orderId: row.orderId,
    date: row.date,
    countryId: row.countryId,
    countryName: row.countryName ?? null,
    currencyCode: row.currencyCode,
    channelCode: row.channelCode ?? null,
    channelName: row.channelName ?? null,
    providerId: row.providerId ?? null,
    providerName: row.providerName ?? null,
    brandId: row.brandId ?? null,
    brandName: row.brandName ?? null,
    denominationId: row.denominationId ?? null,
    denominationName: row.denominationName ?? null,
    providerPrice: row.providerPrice ?? null,
    providerCurrency: row.providerCurrency ?? null,
    billerTrxNo: row.billerTrxNo ?? null,
    providerTrxNo: row.providerTrxNo ?? null,
    receiptNumber: row.receiptNumber ?? null,
    isPriced: row.isPriced,
    collected: row.collected,
    discount: row.discount,
    channelFee: row.channelFee ?? null,
    netRevenue: row.netRevenue ?? null,
    providerCost: row.providerCost ?? null,
    grossProfit: row.grossProfit ?? null,
    mgmtFee: row.mgmtFee ?? null,
    netProfit: row.netProfit ?? null,
    partnerShare: row.partnerShare ?? null,
    retained: row.retained ?? null,
    realizedMarginPct: row.realizedMarginPct ?? null,
  };
}

/**
 * The report's completed orders, one row each — brand, denomination, payment
 * channel, provider and the money (reports.financial.read). Same filters as
 * the report; paged and sorted on the server (page size at most 100).
 */
export async function getFinancialTransactions(
  params: FinancialTransactionParams,
  signal?: AbortSignal,
): Promise<PagedResponse<FinancialTransaction>> {
  const page = await http.get<AdminPage<FinancialTransaction>>(
    '/admin/reports/financial/transactions',
    { params, signal },
  );
  return {
    items: page.items.map(toFinancialTransaction),
    page: page.page,
    pageSize: page.pageSize,
    totalItems: page.totalCount,
    totalPages: page.totalPages,
  };
}

/** The payment channels, providers and brands to filter reports by, in the staff member's countries. */
export async function getReportFilterOptions(
  countryId: number | undefined,
  signal?: AbortSignal,
): Promise<ReportFilterOptions> {
  const options = await http.get<Partial<ReportFilterOptions>>('/admin/reports/filters', {
    params: { countryId },
    signal,
  });
  return {
    channels: options.channels ?? [],
    providers: options.providers ?? [],
    brands: options.brands ?? [],
  };
}

/** A brand's denominations sold in the staff member's countries (the Denomination filter). */
export async function getReportDenominationOptions(
  brandId: number,
  countryId: number | undefined,
  signal?: AbortSignal,
): Promise<ReportFilterOption[]> {
  return http.get<ReportFilterOption[]>('/admin/reports/filters/denominations', {
    params: { brandId, countryId },
    signal,
  });
}
